import { useState } from 'react';
import { Box, IconButton, Link, TableCell, TableRow, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Loading } from 'framework/ui/mui-builder';
import { ORIGIN_CHANNEL_LABELS, TREE_CHILDREN_PAGE_SIZE } from '../../constants';
import { useCoreServiceClient } from '../../api/coreServiceClient';
import OriginDot from '../../components/OriginDot';
import SlaText from '../../components/SlaText';
import StateChip from '../../components/StateChip';
import type { OriginDto, ProcessRunDto, TicketRowDto } from '../../types';
import { formatDate, formatNumber, getErrorMessage } from '../../utils/format';
import { secondsUntil } from '../../utils/sla';

const COLUMNS_WITH_ORIGIN = 6;
const COLUMNS_IN_RUN = 5;

type LoadedChildren = { rows: TicketRowDto[]; total: number };

const useLazyChildren = (loader: () => Promise<LoadedChildren>) => {
  const [isOpen, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<Nullable<string>>(null);
  const [children, setChildren] = useState<Nullable<LoadedChildren>>(null);

  const toggle = async () => {
    if (isOpen) {
      setOpen(false);

      return;
    }

    setOpen(true);
    setLoading(true);
    setError(null);

    try {
      setChildren(await loader());
    } catch (reason) {
      setError(getErrorMessage(reason));
    } finally {
      setLoading(false);
    }
  };

  return { isOpen, isLoading, error, children, toggle };
};

const Expander = ({
  isVisible,
  isOpen,
  label,
  onClick,
}: {
  isVisible: boolean;
  isOpen: boolean;
  label: string;
  onClick: () => void;
}) =>
  isVisible ? (
    <IconButton size="small" aria-label={label} title={label} aria-expanded={isOpen} onClick={onClick}>
      {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
    </IconButton>
  ) : null;

const StatusRows = ({
  isLoading,
  error,
  colSpan,
  extra,
}: {
  isLoading: boolean;
  error: Nullable<string>;
  colSpan: number;
  extra?: string;
}) => {
  if (!isLoading && !error && !extra) {
    return null;
  }

  return (
    <TableRow sx={{ backgroundColor: 'background.default' }}>
      <TableCell />
      <TableCell colSpan={colSpan - 1}>
        {isLoading ? <Loading isInitialPosition /> : null}
        {error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : null}
        {extra ? (
          <Typography variant="body2" color="text.secondary">
            {extra}
          </Typography>
        ) : null}
      </TableCell>
    </TableRow>
  );
};

const FromCell = ({
  origin,
  onOpenProcess,
}: {
  origin?: Nullable<OriginDto>;
  onOpenProcess: (processKey: string) => void;
}) => {
  if (!origin) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }

  if (origin.kind === 'PROCESS') {
    const run = origin.businessKey
      ? origin.businessKey
      : origin.processInstanceId
        ? `run ${origin.processInstanceId.slice(0, 12)}`
        : 'run not identified';

    return (
      <Box sx={{ fontSize: 12, maxWidth: 190 }}>
        <Link
          component="button"
          type="button"
          underline="hover"
          title="Show this process in “By process”"
          onClick={() => onOpenProcess(origin.processKey || 'unidentified')}
          sx={{ color: 'secondary.main', font: 'inherit', textAlign: 'left' }}
        >
          <OriginDot kind="process" />
          {origin.process}
        </Link>
        <Typography variant="caption" color="text.secondary" component="div">
          {run}
          {origin.oneStopService ? ' · via One-Stop' : ''}
          {origin.partial ? ' · engine not connected' : ''}
        </Typography>
      </Box>
    );
  }

  const who = origin.initiator || ORIGIN_CHANNEL_LABELS[origin.channel] || 'unknown';
  const via = origin.channel === 'ONE_STOP' ? origin.oneStopService || 'One-Stop' : '';

  return (
    <Box sx={{ fontSize: 12, maxWidth: 190 }}>
      <Box component="span" sx={{ color: 'info.main' }}>
        <OriginDot kind="person" />
        {who}
      </Box>
      {via ? (
        <Typography variant="caption" color="text.secondary" component="div">
          {via}
        </Typography>
      ) : null}
    </Box>
  );
};

const TicketSla = ({ ticket }: { ticket: TicketRowDto }) => {
  const seconds = ticket.slaDeadline ? secondsUntil(ticket.slaDeadline) : null;
  const fallback = ticket.stateKind === 'STEADY' ? 'ongoing' : ticket.stateKind === 'TRANSIENT' ? 'no SLA' : undefined;

  return <SlaText seconds={seconds} fallback={fallback} dueDate={ticket.dueDate} />;
};

type TicketRowProps = {
  ticket: TicketRowDto;
  depth: number;
  /** Rows under a process run have no "From" column. */
  inRun?: boolean;
  openOnly: boolean;
  onOpenProcess: (processKey: string) => void;
};

/** A ticket line; its subtasks expand inline (recursively). */
export const TicketRow = ({ ticket, depth, inRun = false, openOnly, onOpenProcess }: TicketRowProps) => {
  const client = useCoreServiceClient();
  const subtasks = useLazyChildren(async () => {
    const page = await client.getTreeChildren(ticket.id, { openOnly, pageSize: TREE_CHILDREN_PAGE_SIZE });

    return { rows: page.tickets?.result ?? [], total: page.tickets?.totalElements ?? 0 };
  });
  const columns = inRun ? COLUMNS_IN_RUN : COLUMNS_WITH_ORIGIN;
  const owner = ticket.owner ? (
    ticket.owner
  ) : ticket.queueSize ? (
    <StateChip label={`queue · ${ticket.queueSize}`} />
  ) : (
    <Typography variant="body2" color="warning.main" component="span">
      nobody
    </Typography>
  );
  const meta = [`#${ticket.issueId}`, ticket.tracker, ticket.project, ticket.service].filter(Boolean).join(' · ');

  return (
    <>
      <TableRow
        hover
        sx={{
          backgroundColor: depth ? 'background.default' : undefined,
          opacity: ticket.stateKind === 'STEADY' ? 0.85 : 1,
        }}
      >
        <TableCell sx={{ width: 40, paddingLeft: 1 + depth * 2.25 }}>
          <Expander
            isVisible={ticket.childCount > 0}
            isOpen={subtasks.isOpen}
            label="Show subtasks"
            onClick={subtasks.toggle}
          />
        </TableCell>
        <TableCell sx={{ maxWidth: 460 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {depth ? '└ ' : ''}
            {ticket.url ? (
              <Link href={ticket.url} target="_blank" rel="noopener noreferrer" color="inherit" underline="hover">
                {ticket.title}
              </Link>
            ) : (
              ticket.title
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {meta}
          </Typography>
        </TableCell>
        <TableCell>
          <StateChip label={ticket.stateName || ticket.state} />
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{owner}</TableCell>
        <TableCell>
          <TicketSla ticket={ticket} />
        </TableCell>
        {inRun ? null : (
          <TableCell>
            <FromCell origin={ticket.origin} onOpenProcess={onOpenProcess} />
          </TableCell>
        )}
      </TableRow>
      {subtasks.isOpen ? (
        <>
          {subtasks.children?.rows.map(child => (
            <TicketRow
              key={child.id}
              ticket={child}
              depth={depth + 1}
              inRun={inRun}
              openOnly={openOnly}
              onOpenProcess={onOpenProcess}
            />
          ))}
          <StatusRows
            isLoading={subtasks.isLoading}
            error={subtasks.error}
            colSpan={columns}
            extra={
              subtasks.children && subtasks.children.total > subtasks.children.rows.length
                ? `${formatNumber(subtasks.children.total - subtasks.children.rows.length)} more subtasks — use search to narrow`
                : undefined
            }
          />
        </>
      ) : null}
    </>
  );
};

type RunRowProps = {
  run: ProcessRunDto;
  nodeId: string;
  openOnly: boolean;
  onOpenProcess: (processKey: string) => void;
};

/** A process run; expands into the tasks it created (flat: a process task may be somebody's subtask). */
export const RunRow = ({ run, nodeId, openOnly, onOpenProcess }: RunRowProps) => {
  const client = useCoreServiceClient();
  const tasks = useLazyChildren(async () => {
    const page = await client.getTreeChildren(nodeId, { openOnly, run: run.runKey, pageSize: TREE_CHILDREN_PAGE_SIZE });

    return { rows: page.tickets?.result ?? [], total: page.tickets?.totalElements ?? 0 };
  });
  const title = run.unidentified
    ? 'Created by the process engine, run not identified'
    : run.businessKey || (run.instanceId ? `run ${run.instanceId}` : run.runKey);
  const started = run.startedAt
    ? ` · started ${formatDate(run.startedAt)}${run.startedBy ? ` by ${run.startedBy}` : ''}`
    : '';
  const nextSeconds = run.nextDeadline ? secondsUntil(run.nextDeadline) : null;

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 40, paddingLeft: 1 }}>
          <Expander isVisible isOpen={tasks.isOpen} label="Show tasks of this run" onClick={tasks.toggle} />
        </TableCell>
        <TableCell sx={{ maxWidth: 460 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500 }}
            title={`${run.processKey ?? ''}${run.instanceId ? ` · ${run.instanceId}` : ''}`}
          >
            <OriginDot kind="process" />
            {run.process}
            {run.active === true ? (
              <Typography component="span" variant="caption" color="success.main" sx={{ marginLeft: 1 }}>
                running
              </Typography>
            ) : null}
            {run.active === false ? (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ marginLeft: 1 }}>
                finished
              </Typography>
            ) : null}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {title}
            {started}
          </Typography>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <b>{formatNumber(run.open)}</b>{' '}
          <Typography component="span" variant="caption" color="text.secondary">
            open{run.tasks !== run.open ? ` / ${formatNumber(run.tasks)}` : ''}
          </Typography>
          {run.overdue ? (
            <Typography variant="caption" color="error.main" component="div">
              {formatNumber(run.overdue)} overdue
            </Typography>
          ) : null}
        </TableCell>
        <TableCell>
          {run.waitingFor.length ? (
            <>
              {run.waitingFor.slice(0, 2).join(', ')}
              {run.waitingFor.length > 2 ? (
                <Typography component="span" variant="caption" color="text.secondary">
                  {' '}
                  +{run.waitingFor.length - 2}
                </Typography>
              ) : null}
            </>
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell>
          <SlaText seconds={nextSeconds} fallback="—" />
        </TableCell>
      </TableRow>
      {tasks.isOpen ? (
        <>
          {tasks.children?.rows.map(task => (
            <TicketRow key={task.id} ticket={task} depth={1} inRun openOnly={openOnly} onOpenProcess={onOpenProcess} />
          ))}
          <StatusRows
            isLoading={tasks.isLoading}
            error={tasks.error}
            colSpan={COLUMNS_IN_RUN}
            extra={
              tasks.children && tasks.children.total > tasks.children.rows.length
                ? `${formatNumber(tasks.children.total - tasks.children.rows.length)} more tasks — use search to narrow`
                : undefined
            }
          />
        </>
      ) : null}
    </>
  );
};
